import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Coins, Infinity, Zap, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFreeTrialContext } from '@/contexts/FreeTrialContext';
import { useTranslation } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

interface TokenStatusIndicatorProps {
  className?: string;
  compact?: boolean;
}

export const TokenStatusIndicator: React.FC<TokenStatusIndicatorProps> = ({
  className,
  compact = false
}) => {
  const { t } = useTranslation();
  const { userProfile, isLoading, getAvailableTokens, isAuthenticated } = useAuth();
  const { isInFreeTrial, freeTrialStatus } = useFreeTrialContext();

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
      </div>
    );
  }

  const availableTokens = getAvailableTokens();
  const isUnlimited = availableTokens === -1;

  // 根据 token 数量决定颜色
  const getVariant = () => {
    // 免费试用用户的颜色逻辑
    if (!isAuthenticated && isInFreeTrial) {
      if (availableTokens > 5000) return 'default';
      if (availableTokens > 1000) return 'secondary';
      return 'destructive';
    }
    
    // 登录用户的颜色逻辑
    if (isUnlimited) return 'default';
    if (availableTokens > 1000) return 'default';
    if (availableTokens > 100) return 'secondary';
    if (availableTokens > 0) return 'destructive';
    return 'destructive';
  };

  const formatTokens = (tokens: number) => {
    if (tokens === -1) return t('freeTrial.unlimited');
    // if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    // if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  // 格式化剩余时间
  const formatRemainingTime = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) return t('freeTrial.expired');

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // 如果用户未登录且在免费试用中
  if (!isAuthenticated && isInFreeTrial && freeTrialStatus) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Badge variant={getVariant()} className="flex items-center gap-1">
          <Zap className="w-3 h-3" />
          {compact ? (
            formatTokens(availableTokens)
          ) : (
            <>
              {formatTokens(availableTokens)} {t('freeTrial.freeQuota')}
            </>
          )}
        </Badge>
        
        {/* 剩余时间显示 */}
        {freeTrialStatus.expiresAt && (
          <Badge variant="outline" className="flex items-center gap-1 text-xs">
            <Clock className="w-3 h-3" />
            {formatRemainingTime(freeTrialStatus.expiresAt)}
          </Badge>
        )}
      </div>
    );
  }

  // 如果用户未登录且没有免费试用
  if (!isAuthenticated) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Coins className="w-3 h-3" />
          {compact ? '0' : `0 ${t('freeTrial.tokens')}`}
        </Badge>
      </div>
    );
  }

  // 登录用户的显示逻辑（原有逻辑）
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Badge variant={getVariant()} className="flex items-center gap-1">
        {isUnlimited ? (
          <Infinity className="w-3 h-3" />
        ) : (
          <Coins className="w-3 h-3" />
        )}
        {compact ? (
          formatTokens(availableTokens)
        ) : (
          <>
            {formatTokens(availableTokens)}
            {!isUnlimited && ` ${t('freeTrial.tokens')}`}
          </>
        )}
      </Badge>
      
      {/* 计划类型指示器 */}
      {userProfile?.plan === 'premium' && (
        <Badge variant="outline" className="text-xs">
          {t('freeTrial.pro')}
        </Badge>
      )}
    </div>
  );
}; 